import { useCallback, useState } from "react";
import { Dialog, ProgressBar } from "@ui";
import {
  Upload,
  X,
  FileText,
  Image,
  Table,
  File,
  AlertCircle,
  type LucideIcon,
} from "lucide-react";
import {
  useUploadDocument,
  ALLOWED_TYPES,
  MAX_FILE_SIZE,
  formatFileSize,
} from "@agentic_system/api/document";
import toast from "react-hot-toast";

interface UploadDialogProps {
  open: boolean;
  onClose: () => void;
}

interface FileWithProgress {
  file: File;
  progress: number;
  status: "pending" | "uploading" | "completed" | "error";
  error?: string;
}

const FILE_ICONS: Record<string, LucideIcon> = {
  image: Image,
  pdf: FileText,
  word: FileText,
  excel: Table,
  default: File,
};

function getFileIcon(contentType: string): LucideIcon {
  if (contentType.startsWith("image/")) return FILE_ICONS.image;
  if (contentType === "application/pdf") return FILE_ICONS.pdf;
  if (contentType.includes("wordprocessingml")) return FILE_ICONS.word;
  if (contentType.includes("spreadsheetml")) return FILE_ICONS.excel;
  return FILE_ICONS.default;
}

export function UploadDialog({ open, onClose }: UploadDialogProps) {
  const [files, setFiles] = useState<FileWithProgress[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const uploadMutation = useUploadDocument();

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return "Type de fichier non supporté";
    }
    if (file.size > MAX_FILE_SIZE) {
      return `Fichier trop volumineux (max ${formatFileSize(MAX_FILE_SIZE)})`;
    }
    return null;
  };

  const addFiles = useCallback((newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles);
    const filesToAdd: FileWithProgress[] = fileArray.map((file) => {
      const error = validateFile(file);
      return {
        file,
        progress: 0,
        status: error ? "error" : "pending",
        error: error || undefined,
      };
    });
    setFiles((prev) => [...prev, ...filesToAdd]);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        addFiles(e.dataTransfer.files);
      }
    },
    [addFiles],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files.length > 0) {
        addFiles(e.target.files);
      }
    },
    [addFiles],
  );

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const handleClose = useCallback(() => {
    setFiles([]);
    onClose();
  }, [onClose]);

  const uploadFiles = useCallback(async () => {
    const pendingFiles = files.filter((f) => f.status === "pending");
    let successCount = 0;
    let errorCount = 0;

    for (let i = 0; i < pendingFiles.length; i++) {
      const fileIndex = files.findIndex((f) => f.file === pendingFiles[i].file);

      setFiles((prev) =>
        prev.map((f, idx) =>
          idx === fileIndex ? { ...f, status: "uploading" } : f,
        ),
      );

      try {
        await uploadMutation.mutateAsync({
          file: pendingFiles[i].file,
          onProgress: (progress) => {
            setFiles((prev) =>
              prev.map((f, idx) =>
                idx === fileIndex ? { ...f, progress } : f,
              ),
            );
          },
        });

        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === fileIndex
              ? { ...f, status: "completed", progress: 100 }
              : f,
          ),
        );
        successCount++;
      } catch (error) {
        setFiles((prev) =>
          prev.map((f, idx) =>
            idx === fileIndex
              ? { ...f, status: "error", error: "Échec du téléchargement" }
              : f,
          ),
        );
        errorCount++;
      }
    }

    // Show toast and close dialog when all uploads complete
    if (successCount > 0 && errorCount === 0) {
      toast.success(
        `${successCount} fichier${successCount > 1 ? "s" : ""} téléchargé${successCount > 1 ? "s" : ""}`,
      );
      handleClose();
    } else if (successCount > 0 && errorCount > 0) {
      toast.error(
        `${errorCount} fichier${errorCount > 1 ? "s" : ""} non téléchargé${errorCount > 1 ? "s" : ""}`,
      );
    } else if (errorCount > 0) {
      toast.error("Échec du téléchargement");
    }
  }, [files, uploadMutation, handleClose]);

  const pendingCount = files.filter((f) => f.status === "pending").length;
  const isUploading = files.some((f) => f.status === "uploading");

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title="Télécharger des fichiers"
      subtitle="Glissez-déposez des fichiers ou cliquez pour parcourir"
      className="max-w-lg"
    >
      <div
        className={`
          border-2 border-dashed rounded-lg p-8 text-center transition-colors
          ${dragActive ? "border-default-500 bg-default-50" : "border-gray-300"}
        `}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        role="button"
        aria-label="Drop zone for file upload"
      >
        <Upload className="size-8 text-gray-400 mx-auto mb-3" />
        <p className="text-sm text-gray-600 mb-1">Glissez-déposez des fichiers ici</p>
        <p className="text-xs text-gray-400 mb-4">
          PDF, Word, Excel, Images jusqu'à 30 Mo
        </p>
        <input
          type="file"
          multiple
          accept={ALLOWED_TYPES.join(",")}
          onChange={handleFileSelect}
          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border file:border-gray-300 file:text-sm file:font-medium file:bg-white file:text-gray-700 hover:file:bg-gray-50 file:cursor-pointer"
        />
      </div>

      {files.length > 0 && (
        <div className="mt-4 space-y-2 max-h-60 overflow-y-auto">
          {files.map((fileItem, index) => {
            const Icon = getFileIcon(fileItem.file.type);
            return (
              <div
                key={`${fileItem.file.name}-${index}`}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
              >
                <Icon className="size-5 text-gray-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {fileItem.file.name}
                  </p>
                  <p className="text-xs text-gray-500">
                    {formatFileSize(fileItem.file.size)}
                  </p>
                  {fileItem.status === "uploading" && (
                    <div className="mt-1">
                      <ProgressBar value={fileItem.progress} size="sm" />
                    </div>
                  )}
                  {fileItem.status === "error" && (
                    <p className="text-xs text-red-600 flex items-center gap-1 mt-1">
                      <AlertCircle className="size-3" />
                      {fileItem.error}
                    </p>
                  )}
                </div>
                {(fileItem.status === "pending" ||
                  fileItem.status === "error") && (
                  <button
                    type="button"
                    onClick={() => removeFile(index)}
                    className="text-gray-400 hover:text-gray-600"
                    aria-label={`Remove ${fileItem.file.name}`}
                  >
                    <X className="size-4" />
                  </button>
                )}
                {fileItem.status === "completed" && (
                  <span className="text-green-600 text-xs font-medium">
                    Terminé
                  </span>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={handleClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800"
          disabled={isUploading}
        >
          {files.every((f) => f.status === "completed") ? "Fermer" : "Annuler"}
        </button>
        {pendingCount > 0 && (
          <button
            type="button"
            onClick={uploadFiles}
            disabled={isUploading}
            className="px-4 py-2 bg-default-600 text-white text-sm font-medium rounded-md hover:bg-default-700 disabled:opacity-50"
          >
            {isUploading
              ? "Téléchargement..."
              : `Télécharger ${pendingCount} fichier${pendingCount > 1 ? "s" : ""}`}
          </button>
        )}
      </div>
    </Dialog>
  );
}
